'use client'

import Link from 'next/link'
import React, { createElement, useState, type ComponentType } from 'react'

import SpeaksLanguages from '@/legacy-src/components/SpeaksLanguages'
import TherapistCategory from '@/legacy-src/components/TherapistCategory'
import { type TherapistCategoryType } from '@/legacy-src/hooks/useTherapistCategories'

import { ButtonLink } from '@/src/domains/design-system/components/Button'
import { Tag } from '@/src/domains/design-system/components/Tag'
import { Text } from '@/src/domains/design-system/components/Text'
import { TherapistCardEventType } from '@/src/domains/therapist/components/TherapistCardEventType'

import { type SpecializationOptionsType, type TherapistType } from '../types/TherapistStateType'
import { getTranslate } from '../utilities/i18n'
import { getFullName } from '../utilities/name'
import Modal from './modal/Modal'
import Recommendations from './profiles/Recommendations'
import TherapistProfileImage from './profiles/TherapistProfileImage'
import VerifiedTraining from './profiles/VerifiedTraining'
import TherapeuticStyleProfile from './recommendedTherapist/TherapeuticStyleProfile'
import YoutubeVideo from './YoutubeVideo'

type Props = {
  therapist: TherapistType
  href: any
  showTherapeuticScale?: boolean
  allowBookingForFree?: boolean
  displayedPrice: number
  therapistId: number
  specializations: SpecializationOptionsType
  categories?: TherapistCategoryType[]
  prefetch?: boolean
  openModalFromOutside?: (videoId: string) => void | null
  displayLanguages?: boolean
  EventTypeComponent?: ComponentType<{
    therapist: TherapistType
  }> | null
}

const TherapistCardContent: React.FC<Props> = ({
  href,
  showTherapeuticScale,
  allowBookingForFree,
  displayedPrice,
  therapistId,
  specializations,
  therapist,
  prefetch,
  categories,
  displayLanguages = false,
  EventTypeComponent = TherapistCardEventType,
}) => {
  const { t } = getTranslate()

  const [videoVisible, setVideoVisible] = useState(false)

  const {
    firstName,
    lastName,
    image,
    titleAfter,
    titleBefore,
    experience,
    majorSpecializations = [],
    minorSpecializations = [],
    description,
    verifiedCertificate,
    recommendations,
    videoId,
  } = therapist

  const category = categories?.find((category) => category.id === therapist.therapistCategoryId)

  const displayRecommendations = Boolean(recommendations)
  const displayCategories = Boolean(categories && categories.length > 0)
  const displayVerifiedTraining = !displayCategories && typeof verifiedCertificate === 'number'

  return (
    <div className="flex w-full flex-col rounded-xl bg-white">
      <div className="w-full p-5 pb-0">
        <div className="mb-6 flex items-center">
          <TherapistProfileImage
            {...{ href, prefetch }}
            identifier={`${firstName} ${lastName}`}
            openModal={videoId ? () => setVideoVisible(true) : null}
            source={image}
            height={110}
            width={100}
            className="mr-4 flex-shrink-0"
          />
          <div className="flex flex-col items-start">
            <Tag variant="yellow" size="xs" className="mb-0.5">
              {t('therapistProfile.experience', { experience })}
            </Tag>
            <Link className="font-serif text-xl font-semibold leading-5 text-petrol-500" {...{ prefetch, href }}>
              {getFullName({ person: { firstName, lastName }, titleAfter, titleBefore })}
            </Link>
            <Text element="span" variant="sm" className="mt-1">
              {allowBookingForFree
                ? t('therapistProfile.priceFirstFree')
                : t('component.therapistProfileView.price', { price: displayedPrice })}
            </Text>
          </div>
        </div>
        {majorSpecializations.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {majorSpecializations.map((tag) => (
              <Tag variant="sand" size="s" key={tag}>
                {specializations?.[tag]}
              </Tag>
            ))}
            {minorSpecializations.length > 0 ? (
              <Tag variant="sand" size="s">
                + {minorSpecializations.length}
              </Tag>
            ) : null}
          </div>
        ) : null}
        {(displayRecommendations || displayVerifiedTraining || displayCategories) && (
          <div className="mt-6 flex flex-col gap-3">
            {displayVerifiedTraining ? (
              <VerifiedTraining verified={verifiedCertificate} />
            ) : displayCategories && category ? (
              <TherapistCategory category={category} />
            ) : null}
            {displayRecommendations && <Recommendations recommendations={recommendations} />}
            {EventTypeComponent === null ? null : createElement(EventTypeComponent, { therapist })}
            {displayLanguages && <SpeaksLanguages languages={therapist.languages || []} />}
          </div>
        )}
        {description && <div className="mt-6 text-sm">{description}</div>}
      </div>
      <div className="grow"></div>
      <div className="mt-6 w-full px-5">
        <ButtonLink className="mt-4 w-full" size="small" {...{ prefetch, href }}>
          {t('therapistProfile.openProfile')}
        </ButtonLink>
      </div>
      <div className="w-full p-5 pt-0">
        {showTherapeuticScale && <TherapeuticStyleProfile therapistId={therapistId} listing />}
      </div>
      <Modal className="block" closeModal={() => setVideoVisible(false)} size="lg" video visible={videoVisible}>
        <YoutubeVideo videoId={videoId} />
      </Modal>
    </div>
  )
}

export default TherapistCardContent
